import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Plus, Calendar, X, Search, Trash2, Pencil } from 'lucide-react'
import { getTasks, createTask as createTaskSvc, updateTask as updateTaskSvc, deleteTask as deleteTaskSvc } from '../services/tasks.service'
import { getTeamMembers } from '../services/team.service'
import { supabase } from '../lib/supabase'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import type { Task } from '../types'

type StatusCol = { key: Task['status']; label: string }
const COLUMNS: StatusCol[] = [
  { key: 'todo',        label: 'To Do' },
  { key: 'in-progress', label: 'In Progress' },
  { key: 'review',      label: 'Review' },
  { key: 'done',        label: 'Done' },
]

const PRIORITY_STYLE: Record<Task['priority'], { bg: string; color: string }> = {
  urgent: { bg: 'rgba(204,51,0,0.18)',   color: '#cc3300' },
  high:   { bg: 'rgba(255,140,0,0.18)',  color: '#ff8c00' },
  medium: { bg: 'rgba(255,198,0,0.13)',  color: '#ffc600' },
  low:    { bg: 'rgba(85,85,85,0.2)',    color: '#555555' },
}

// Team loaded from Supabase profiles at runtime
let TEAM: string[] = []

function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

function isOverdue(dueDate: string) {
  return new Date(dueDate) < new Date()
}

// ── Add Task Modal ─────────────────────────────────────────────
function AddTaskModal({ onClose, onAdd, teamMembers }: { onClose: () => void; onAdd: (t: Omit<Task, 'id' | 'createdAt'>) => void; teamMembers: string[] }) {
  const today = new Date().toISOString().split('T')[0]
  const [form, setForm] = useState({
    title:       '',
    description: '',
    priority:    'medium' as Task['priority'],
    status:      'todo' as Task['status'],
    assignee:    teamMembers[0] || '',
    projectName: '',
    dueDate:     today,
    tagsRaw:     '',
  })
  const [error, setError] = useState('')

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) { setError('Title is required.'); return }
    if (!form.dueDate)       { setError('Due date is required.'); return }
    const tags = form.tagsRaw.split(',').map(t => t.trim()).filter(Boolean)
    const newTask: Omit<Task, 'id' | 'createdAt'> = {
      title:       form.title.trim(),
      description: form.description.trim(),
      priority:    form.priority,
      status:      form.status,
      assignee:    form.assignee,
      projectName: form.projectName.trim() || undefined,
      dueDate:     form.dueDate,
      tags,
    }
    onAdd(newTask)
    onClose()
  }

  const inputCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelCls = "block text-xs font-semibold mb-1.5"
  const labelStyle = { color: '#a0a0a0' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>New Task</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className={labelCls} style={labelStyle}>TITLE *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="What needs to be done?" className={inputCls} style={inputStyle} autoFocus
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          {/* Description */}
          <div>
            <label className={labelCls} style={labelStyle}>DESCRIPTION</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Optional details…" rows={2}
              className={inputCls} style={{ ...inputStyle, resize: 'none' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          {/* Priority + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls} style={labelStyle}>PRIORITY</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}
                className={inputCls} style={inputStyle}>
                {(['urgent', 'high', 'medium', 'low'] as const).map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} style={labelStyle}>STATUS</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className={inputCls} style={inputStyle}>
                {COLUMNS.map(c => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Assignee + Due date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls} style={labelStyle}>ASSIGNEE</label>
              <select value={form.assignee} onChange={e => set('assignee', e.target.value)}
                className={inputCls} style={inputStyle}>
                {teamMembers.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls} style={labelStyle}>DUE DATE *</label>
              <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)}
                className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }}
                onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
            </div>
          </div>

          {/* Project */}
          <div>
            <label className={labelCls} style={labelStyle}>PROJECT NAME</label>
            <input value={form.projectName} onChange={e => set('projectName', e.target.value)}
              placeholder="e.g. Astra Fintech — SEO" className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          {/* Tags */}
          <div>
            <label className={labelCls} style={labelStyle}>TAGS <span style={{ color: '#555', fontWeight: 400 }}>(comma separated)</span></label>
            <input value={form.tagsRaw} onChange={e => set('tagsRaw', e.target.value)}
              placeholder="e.g. SEO, Google Ads, Content" className={inputCls} style={inputStyle}
              onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
              onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#ffc600', color: '#000' }}>
              Add Task
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Task Card ──────────────────────────────────────────────────
function TaskCard({ task, onClick }: { task: Task; onClick: () => void }) {
  const p = PRIORITY_STYLE[task.priority]
  const overdue = task.status !== 'done' && isOverdue(task.dueDate)
  return (
    <div className="rounded-xl p-3 mb-2 cursor-pointer transition-colors"
      style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.12)' }}
      onMouseEnter={e => (e.currentTarget.style.background = '#242424')}
      onMouseLeave={e => (e.currentTarget.style.background = '#1a1a1a')}
      onClick={onClick}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs px-2 py-0.5 rounded font-semibold capitalize" style={{ background: p.bg, color: p.color }}>{task.priority}</span>
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ background: '#0f0f0f', color: '#ffc600', fontSize: '10px' }}>
          {initials(task.assignee)}
        </div>
      </div>
      <p className="text-sm font-medium text-white mb-1 leading-snug">{task.title}</p>
      {task.projectName && (
        <p className="text-xs mb-1.5" style={{ color: '#a0a0a0' }}>{task.projectName}</p>
      )}
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-xs" style={{ color: overdue ? '#cc3300' : '#555' }}>
          <Calendar size={10} />
          {new Date(task.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </span>
        {task.tags.length > 0 && (
          <div className="flex gap-1">
            {task.tags.slice(0, 2).map(tag => (
              <span key={tag} className="text-xs px-1.5 py-0.5 rounded"
                style={{ background: 'rgba(255,198,0,0.08)', color: '#ffc600', fontSize: '10px' }}>{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Task Detail Modal ──────────────────────────────────────────
function TaskDetailModal({ task, onClose, onStatusChange, onDelete, onEdit, isAdmin, teamMembers }: {
  task: Task; onClose: () => void
  onStatusChange: (id: string, status: Task['status']) => void
  onDelete: (id: string) => void
  onEdit: (id: string, changes: Partial<Task>) => void
  isAdmin: boolean
  teamMembers: string[]
}) {
  const p = PRIORITY_STYLE[task.priority]
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editing, setEditing] = useState(false)

  const [editForm, setEditForm] = useState({
    title:       task.title,
    description: task.description ?? '',
    priority:    task.priority,
    dueDate:     task.dueDate,
    assignee:    task.assignee,
    projectName: task.projectName ?? '',
    tagsRaw:     task.tags.join(', '),
  })

  const setF = (k: string, v: string) => setEditForm(f => ({ ...f, [k]: v }))

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    const tags = editForm.tagsRaw.split(',').map(t => t.trim()).filter(Boolean)
    const changes: Partial<Task> = {
      title:       editForm.title.trim(),
      description: editForm.description.trim(),
      priority:    editForm.priority as Task['priority'],
      dueDate:     editForm.dueDate,
      assignee:    editForm.assignee,
      projectName: editForm.projectName.trim() || undefined,
      tags,
    }
    onEdit(task.id, changes)
    setEditing(false)
  }

  const inputCls  = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelCls  = "block text-xs font-semibold mb-1.5"
  const labelStyle = { color: '#a0a0a0' }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>

        {editing ? (
          /* ── Edit form ── */
          <>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Edit Task</h2>
              <button onClick={() => setEditing(false)} style={{ color: '#555' }}><X size={18} /></button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className={labelCls} style={labelStyle}>TITLE *</label>
                <input value={editForm.title} onChange={e => setF('title', e.target.value)}
                  className={inputCls} style={inputStyle} autoFocus
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>DESCRIPTION</label>
                <textarea value={editForm.description} onChange={e => setF('description', e.target.value)}
                  rows={2} className={inputCls} style={{ ...inputStyle, resize: 'none' }}
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} style={labelStyle}>PRIORITY</label>
                  <select value={editForm.priority} onChange={e => setF('priority', e.target.value)}
                    className={inputCls} style={inputStyle}>
                    {(['urgent', 'high', 'medium', 'low'] as const).map(p => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>DUE DATE</label>
                  <input type="date" value={editForm.dueDate} onChange={e => setF('dueDate', e.target.value)}
                    className={inputCls} style={{ ...inputStyle, colorScheme: 'dark' }}
                    onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                    onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
                </div>
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>ASSIGNEE</label>
                <select value={editForm.assignee} onChange={e => setF('assignee', e.target.value)}
                  className={inputCls} style={inputStyle}>
                  {teamMembers.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>PROJECT NAME</label>
                <input value={editForm.projectName} onChange={e => setF('projectName', e.target.value)}
                  className={inputCls} style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>TAGS <span style={{ color: '#555', fontWeight: 400 }}>(comma separated)</span></label>
                <input value={editForm.tagsRaw} onChange={e => setF('tagsRaw', e.target.value)}
                  className={inputCls} style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditing(false)}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#ffc600', color: '#000' }}>
                  Save
                </button>
              </div>
            </form>
          </>
        ) : (
          /* ── Detail view ── */
          <>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 mr-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-0.5 rounded font-semibold capitalize" style={{ background: p.bg, color: p.color }}>{task.priority}</span>
                </div>
                <h2 className="text-base font-bold text-white leading-snug" style={{ fontFamily: 'Syne, sans-serif' }}>{task.title}</h2>
                {task.projectName && <p className="text-xs mt-1" style={{ color: '#a0a0a0' }}>{task.projectName}</p>}
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button onClick={() => setEditing(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs"
                    style={{ color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.15)' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ffc600'; e.currentTarget.style.borderColor = 'rgba(255,198,0,0.4)' }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#a0a0a0'; e.currentTarget.style.borderColor = 'rgba(255,198,0,0.15)' }}>
                    <Pencil size={13} /> Edit
                  </button>
                )}
                <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
              </div>
            </div>

            {task.description && (
              <p className="text-sm mb-4 leading-relaxed" style={{ color: '#a0a0a0' }}>{task.description}</p>
            )}

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg p-3" style={{ background: '#1a1a1a' }}>
                <p className="text-xs mb-1" style={{ color: '#555' }}>ASSIGNEE</p>
                <p className="text-sm font-semibold text-white">{task.assignee}</p>
              </div>
              <div className="rounded-lg p-3" style={{ background: '#1a1a1a' }}>
                <p className="text-xs mb-1" style={{ color: '#555' }}>DUE DATE</p>
                <p className="text-sm font-semibold text-white">
                  {new Date(task.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>

            {task.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {task.tags.map(tag => (
                  <span key={tag} className="text-xs px-2 py-0.5 rounded"
                    style={{ background: 'rgba(255,198,0,0.08)', color: '#ffc600' }}>{tag}</span>
                ))}
              </div>
            )}

            {/* Move to next status */}
            <div className="space-y-2">
              <p className="text-xs font-semibold mb-2" style={{ color: '#555' }}>MOVE TO</p>
              <div className="flex gap-2 flex-wrap">
                {COLUMNS.filter(c => c.key !== task.status).map(c => (
                  <button key={c.key}
                    onClick={() => { onStatusChange(task.id, c.key); onClose() }}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.15)' }}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-2 mt-4">
              {confirmDelete ? (
                <>
                  <span className="flex-1 text-xs flex items-center" style={{ color: '#a0a0a0' }}>Delete this task?</span>
                  {isAdmin && (
                    <button onClick={() => { onDelete(task.id); onClose() }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold"
                      style={{ background: 'rgba(204,51,0,0.15)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.35)' }}>
                      Yes, delete
                    </button>
                  )}
                  <button onClick={() => setConfirmDelete(false)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {isAdmin && (
                    <button onClick={() => setConfirmDelete(true)}
                      className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm"
                      style={{ color: '#555', border: '1px solid rgba(255,255,255,0.06)' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#cc3300'; e.currentTarget.style.borderColor = 'rgba(204,51,0,0.3)' }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#555'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)' }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                  <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                    style={{ background: '#ffc600', color: '#000' }}>Close</button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const COL_ACCENT: Record<Task['status'], string> = {
  'todo':        '#555',
  'in-progress': '#ffc600',
  'review':      '#ff8c00',
  'done':        '#32c864',
}

// ── Main page ──────────────────────────────────────────────────
export default function Tasks() {
  const [tasks, setTasks]               = useState<Task[]>([])
  const [teamMembers, setTeamMembers]   = useState<string[]>([])
  const [priorityFilter, setPriorityFilter] = useState<Task['priority'] | 'all'>('all')
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all')
  const [search, setSearch]             = useState('')
  const [showAdd, setShowAdd]           = useState(false)
  const [selected, setSelected]         = useState<Task | null>(null)

  // Load team members — primary: team_members table, fallback: profiles if table is empty
  useEffect(() => {
    Promise.all([
      getTeamMembers(),
      supabase.from('profiles').select('full_name').eq('is_active', true),
    ]).then(([members, { data: profileData }]) => {
      const teamNames = members.filter(m => m.status === 'active').map(m => m.name).filter(Boolean)
      const profileNames = (profileData ?? []).map((p: any) => p.full_name as string).filter(Boolean)
      // Prefer team_members; fall back to profiles only if team_members is empty
      const allNames = teamNames.length > 0
        ? teamNames
        : [...new Set([...teamNames, ...profileNames])]
      TEAM = allNames
      setTeamMembers(allNames)
    }).catch(console.error)
  }, [])

  // Tasks show ALL regardless of date range — it's a task board, not a report
  const allAssignees = [...new Set(tasks.map(t => t.assignee).filter(Boolean))]

  const counts = {
    todo:          tasks.filter(t => t.status === 'todo').length,
    'in-progress': tasks.filter(t => t.status === 'in-progress').length,
    review:        tasks.filter(t => t.status === 'review').length,
    done:          tasks.filter(t => t.status === 'done').length,
  }

  const filtered = tasks.filter(t => {
    const pOk = priorityFilter === 'all' || t.priority === priorityFilter
    const aOk = assigneeFilter === 'all' || t.assignee === assigneeFilter
    const sOk = search === '' ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      (t.projectName ?? '').toLowerCase().includes(search.toLowerCase()) ||
      t.assignee.toLowerCase().includes(search.toLowerCase())
    return pOk && aOk && sOk
  })

  const colTasks = (status: Task['status']) => filtered.filter(t => t.status === status)

  const { toast }       = useToast()
  const { addActivity } = useActivity()
  const isAdmin         = useIsAdmin()
  const location        = useLocation()

  useEffect(() => {
    getTasks().then(data => {
      setTasks(data)
      const s = location.state as any
      if (s?.openAdd) { setShowAdd(true); window.history.replaceState({}, '') }
      else if (s?.viewTask) {
        const task = data.find(t => t.id === s.viewTask)
        if (task) setSelected(task)
        window.history.replaceState({}, '')
      }
    }).catch(console.error)
  }, [])

  const handleAdd = async (task: Omit<Task, 'id' | 'createdAt'>) => {
    try {
      const saved = await createTaskSvc(task)
      setTasks(prev => [saved, ...prev])
      toast(`Task "${saved.title.slice(0,40)}" added`)
      addActivity({ type: 'task_added', emoji: '✅', description: `New task: ${saved.title.slice(0,40)}`, detail: `${saved.priority} priority · ${saved.assignee}` })
    } catch { toast('Failed to add task', 'error') }
  }

  const handleStatusChange = async (id: string, status: Task['status']) => {
    const labels: Record<Task['status'], string> = { 'todo': 'To Do', 'in-progress': 'In Progress', 'review': 'Review', 'done': 'Done ✓' }
    try { await updateTaskSvc(id, { status }) } catch { toast('Update failed', 'error'); return }
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t))
    setSelected(prev => prev?.id === id ? { ...prev, status } : prev)
    toast(`Task moved to ${labels[status]}`, status === 'done' ? 'success' : 'info')
    if (status === 'done') {
      const task = tasks.find(t => t.id === id)
      if (task) addActivity({ type: 'task_done', emoji: '🎯', description: `Task completed: ${task.title.slice(0,40)}`, detail: task.assignee })
    }
  }

  const handleDelete = async (id: string) => {
    const task = tasks.find(t => t.id === id)
    try {
      await deleteTaskSvc(id)
      setTasks(prev => prev.filter(t => t.id !== id))
      if (task) toast(`Task "${task.title.slice(0, 40)}" deleted`, 'error')
    } catch { toast('Delete failed', 'error') }
  }

  const handleEdit = async (id: string, changes: Partial<Task>) => {
    try {
      await updateTaskSvc(id, changes)
      setTasks(prev => prev.map(t => t.id === id ? { ...t, ...changes } : t))
      setSelected(prev => prev?.id === id ? { ...prev, ...changes } : prev)
      toast('Task updated')
    } catch { toast('Failed to update task', 'error') }
  }

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold font-syne text-white">Tasks</h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>
            {counts.todo} todo · {counts['in-progress']} in progress · {counts.review} review · {counts.done} done
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#ffc600', color: '#000' }}>
            <Plus size={15} /> Add Task
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        {/* Search */}
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#555' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search tasks…"
            className="pl-8 pr-3 py-1.5 rounded-lg text-xs outline-none text-white placeholder-gray-600 w-40"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }} />
        </div>
        {/* Priority */}
        <div className="flex gap-1.5">
          {(['all', 'urgent', 'high', 'medium', 'low'] as const).map(p => (
            <button key={p} onClick={() => setPriorityFilter(p)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium capitalize transition-all"
              style={priorityFilter === p
                ? { background: '#ffc600', color: '#000' }
                : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              {p}
            </button>
          ))}
        </div>
        {/* Assignee */}
        <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}
          className="px-3 py-1 rounded-lg text-xs outline-none"
          style={{ background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
          <option value="all">All Assignees</option>
          {allAssignees.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map(({ key, label }) => {
          const col    = colTasks(key)
          const accent = COL_ACCENT[key]
          return (
            <div key={key} className="rounded-xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)', minHeight: 400 }}>
              {/* Accent top bar */}
              <div className="h-0.5 w-full" style={{ background: accent }} />
              <div className="p-3">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold" style={{ color: accent }}>{label}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={{ background: `${accent}20`, color: accent }}>{col.length}</span>
                </div>
                {col.length === 0 ? (
                  <div className="text-center py-10 text-xs" style={{ color: '#333' }}>No tasks</div>
                ) : (
                  col.map(t => <TaskCard key={t.id} task={t} onClick={() => setSelected(t)} />)
                )}
              </div>
            </div>
          )
        })}
      </div>

      {showAdd && <AddTaskModal onClose={() => setShowAdd(false)} onAdd={handleAdd} teamMembers={teamMembers} />}
      {selected && (
        <TaskDetailModal
          task={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
          onDelete={id => { handleDelete(id); setSelected(null) }}
          onEdit={handleEdit}
          teamMembers={teamMembers}
        />
      )}
    </div>
  )
}
